import { mean } from './format';
import { logDate } from './logs';

// Gut symptom rules. Scores are 1–5. A check-in day is a day with at least one
// gut symptom row; on it, an unlogged scored symptom counts as 0, so the daily
// average is always the sum of the 4 scored symptoms ÷ 4 (there is no partial
// state). A day with no check-in is left out of every average, never 0.

/** The 4 symptoms the daily average uses, matched by lowercased name. */
export const SCORED = ['Bloating', 'Abdominal pain', 'Gas', 'Digestive discomfort'];
const SCORED_KEY = Object.fromEntries(SCORED.map((n) => [n.toLowerCase(), n]));

/** The reserved row that marks a symptom-free check-in. */
export const NO_SYMPTOM = 'no gut symptom';

const keyOf = (name) => String(name ?? '').trim().toLowerCase();

/**
 * Raw symptom rows → one entry per check-in day.
 * Same symptom twice on a day: the higher score wins.
 *
 * @param {Array<{date: string, symptom_name: string, symptom_score: number|null}>} rows
 * @returns {Record<string, { sym: Record<string, number>, free: boolean, sum: number, avg: number }>}
 */
export function symptomDays(rows = []) {
  const days = {};
  rows.forEach((r) => {
    const iso = logDate(r);
    if (!iso) return;
    const d = days[iso] || (days[iso] = { sym: {}, noSymptomRow: false });
    const key = keyOf(r.symptom_name);
    if (key === NO_SYMPTOM) {
      d.noSymptomRow = true;
      return;
    }
    const name = SCORED_KEY[key] || String(r.symptom_name).trim();
    const score = Number(r.symptom_score);
    const v = r.symptom_score != null && Number.isFinite(score) ? score : 0;
    d.sym[name] = Math.max(d.sym[name] ?? 0, v);
  });
  Object.values(days).forEach((d) => {
    d.sum = SCORED.reduce((s, n) => s + (d.sym[n] ?? 0), 0);
    d.avg = d.sum / SCORED.length;
    // Symptom-free only on the client's own "No Gut Symptom" entry, never inferred.
    d.free = d.noSymptomRow && Object.values(d.sym).every((v) => v === 0);
    delete d.noSymptomRow;
  });
  return days;
}

/** Every symptom name seen: the 4 scored ones first, then the others (Diarrhoea, Constipation…) A–Z. */
export function symptomNames(days) {
  const others = new Set();
  Object.values(days).forEach((d) => Object.keys(d.sym).forEach((n) => SCORED.includes(n) || others.add(n)));
  return [...SCORED, ...[...others].sort((a, b) => a.localeCompare(b))];
}

/**
 * Check-ins over a span (usually a week).
 * - `sub[name]`: mean over the check-in days, unlogged = 0; null with no check-in.
 * - `freq[name]`: check-in days with that symptom above 0.
 *
 * @param {string[]} isos - The span's days.
 * @param {string[]} names - From symptomNames.
 */
export function symptomStats(days, isos, names) {
  const C = isos.filter((iso) => days[iso]).map((iso) => ({ iso, ...days[iso] }));
  const sub = {};
  const freq = {};
  names.forEach((n) => {
    sub[n] = C.length ? mean(C.map((d) => d.sym[n] ?? 0)) : null;
    freq[n] = C.filter((d) => (d.sym[n] ?? 0) > 0).length;
  });
  const hi = C.reduce((best, d) => (!best || d.avg > best.avg ? d : best), null);
  return {
    nCheck: C.length,
    free: C.filter((d) => d.free).length,
    avg: mean(C.map((d) => d.avg)),
    hi,
    sub,
    freq,
  };
}
